import * as _ from 'lodash';
import { OrganizationType } from 'shared/services/authenticatedUser/types';

export interface UserRole {
    name: string;
    title: string;
    description: string;
    privileges: string[];
}

export enum ManufacturerPrivilege {
    Admin = 'admin',
    BrandManager = 'brandManager',
    ContentManager = 'contentManager',
    RightsManager = 'rightsManager',
    SalesAgent = 'salesAgent',
    MarketingManager = 'marketingManager',
    Member = 'member',
    Viewer = 'viewer'
}

export enum RetailerPrivilege {
    Admin = 'admin',
    MarketingManager = 'marketingManager',
    Buyer = 'buyer',
    Viewer = 'viewer'
}

export enum ManufacturerRoleNames {
    Admin = 'admin',
    BrandManager = 'brandManager',
    ContentManager = 'contentManager',
    SalesManager = 'salesManager',
    RightsManager = 'rightsManager',
    SalesAgent = 'salesAgent',
    MarketingManager = 'marketingManager',
    Member = 'member',
    Viewer = 'viewer'
}

enum RetailerRoleNames {
    Admin = 'admin',
    MarketingManager = 'marketingManager',
    Buyer = 'buyer',
    Viewer = 'viewer'
}

export const manufacturerRoles: UserRole[] = [
    {
        name: ManufacturerRoleNames.Admin,
        title: 'UAC.BRAND.ADMIN.NAME',
        description: 'UAC.BRAND.ADMIN.DESCRIPTION',
        privileges: [ManufacturerPrivilege.Admin]
    },
    {
        name: ManufacturerRoleNames.BrandManager,
        title: 'UAC.BRAND.BRANDMANAGER.NAME',
        description: 'UAC.BRAND.BRANDMANAGER.DESCRIPTION',
        privileges: [
            ManufacturerPrivilege.Member,
            ManufacturerPrivilege.RightsManager,
            ManufacturerPrivilege.ContentManager,
            ManufacturerPrivilege.BrandManager,
            ManufacturerPrivilege.SalesAgent
        ]
    },
    {
        name: ManufacturerRoleNames.ContentManager,
        title: 'UAC.BRAND.CONTENTMANAGER.NAME',
        description: 'UAC.BRAND.CONTENTMANAGER.DESCRIPTION',
        privileges: [
            ManufacturerPrivilege.Member,
            ManufacturerPrivilege.RightsManager,
            ManufacturerPrivilege.ContentManager
        ]
    },
    {
        name: ManufacturerRoleNames.SalesManager,
        title: 'UAC.BRAND.SALESMANAGER.NAME',
        description: 'UAC.BRAND.SALESMANAGER.DESCRIPTION',
        privileges: [
            ManufacturerPrivilege.Member,
            ManufacturerPrivilege.RightsManager,
            ManufacturerPrivilege.SalesAgent
        ]
    },
    {
        name: ManufacturerRoleNames.RightsManager,
        title: 'UAC.BRAND.RIGHTSMANAGER.NAME',
        description: 'UAC.BRAND.RIGHTSMANAGER.DESCRIPTION',
        privileges: [ManufacturerPrivilege.Member, ManufacturerPrivilege.RightsManager]
    },
    {
        name: ManufacturerRoleNames.SalesAgent,
        title: 'UAC.BRAND.SALESAGENT.NAME',
        description: 'UAC.BRAND.SALESAGENT.DESCRIPTION',
        privileges: [ManufacturerPrivilege.Member, ManufacturerPrivilege.SalesAgent]
    },
    {
        name: ManufacturerRoleNames.MarketingManager,
        title: 'UAC.BRAND.MARKETINGMANAGER.NAME',
        description: 'UAC.BRAND.MARKETINGMANAGER.DESCRIPTION',
        privileges: [ManufacturerPrivilege.Member, ManufacturerPrivilege.ContentManager]
    },
    {
        name: ManufacturerRoleNames.Member,
        title: 'UAC.BRAND.MEMBER.NAME',
        description: 'UAC.BRAND.MEMBER.DESCRIPTION',
        privileges: [ManufacturerPrivilege.Member]
    },
    {
        name: ManufacturerRoleNames.Viewer,
        title: 'UAC.RETAILER.VIEWER.NAME',
        description: 'UAC.RETAILER.VIEWER.DESCRIPTION',
        privileges: [ManufacturerPrivilege.Viewer]
    }
];

export const retailerRoles: UserRole[] = [
    {
        name: RetailerRoleNames.Admin,
        title: 'UAC.RETAILER.ADMIN.NAME',
        description: 'UAC.RETAILER.ADMIN.DESCRIPTION',
        privileges: [RetailerPrivilege.Admin]
    },
    {
        name: RetailerRoleNames.MarketingManager,
        title: 'UAC.RETAILER.MARKETINGMANAGER.NAME',
        description: 'UAC.RETAILER.MARKETINGMANAGER.DESCRIPTION',
        privileges: [RetailerPrivilege.MarketingManager]
    },
    {
        name: RetailerRoleNames.Buyer,
        title: 'UAC.RETAILER.BUYER.NAME',
        description: 'UAC.RETAILER.BUYER.DESCRIPTION',
        privileges: [RetailerPrivilege.Buyer]
    },
    {
        name: RetailerRoleNames.Viewer,
        title: 'UAC.RETAILER.VIEWER.NAME',
        description: 'UAC.RETAILER.VIEWER.DESCRIPTION',
        privileges: [RetailerPrivilege.Viewer]
    }
];

export const emptyRole = {
    name: 'empty',
    title: 'MANAGE_TEAM.ROLE_COLUMN',
    description: 'MANAGE_TEAM.ROLE_COLUMN',
    privileges: []
};

export function getRoleNameFromPrivileges(privileges: string[], organizationType?: OrganizationType) {
    switch (organizationType) {
        case OrganizationType.Manufacturer:
            return _.find(manufacturerRoles, (role) => _.isEqual(role.privileges, privileges))?.name;
        case OrganizationType.Retailer:
            return _.find(retailerRoles, (role) => _.isEqual(role.privileges, privileges))?.name;
        case OrganizationType.Admin:
            return undefined;
        default:
            return _.find([...manufacturerRoles, ...retailerRoles], (role) => _.isEqual(role.privileges, privileges))
                ?.name;
    }
}

export function getUserRole(privileges: string[], organizationType: OrganizationType) {
    const roles = organizationType === OrganizationType.Retailer ? retailerRoles : manufacturerRoles;
    return _.find(roles, (role) => _.isEmpty(_.difference(role.privileges, privileges)));
}
